import type { ReactNode } from 'react';

/**
 * The portal's line icons, inline rather than fetched.
 *
 * They are grouped in one file the way `booking-icons` and `policy-icons` already are: each is two
 * or three paths, and a request per icon would be a dozen more things to fail on a screen somebody
 * opened to find out what their bank is waiting for.
 *
 * **Every one of them is `aria-hidden`.** They sit beside a heading or a label that already says
 * what the thing is, so announcing "folder" before "My cases" is noise — and an icon that is the
 * only label is a defect this file cannot fix from here.
 */
function Icon({ children, className = 'size-5' }: { children: ReactNode; className?: string }) {
    return (
        <svg
            className={className}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {children}
        </svg>
    );
}

export function CaseIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M3 8a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
        </Icon>
    );
}

export function CalendarIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M3 10h18M8 3v4M16 3v4" />
        </Icon>
    );
}

export function DocumentIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
            <path d="M14 3v5h5" />
        </Icon>
    );
}

export function SignatureIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M3 19c3 0 3-9 6-9s3 6 6 6 3-4 6-4" />
            <path d="M4 21h16" />
        </Icon>
    );
}

export function BellIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M18 15V10a6 6 0 1 0-12 0v5l-2 3h16Z" />
            <path d="M10 21h4" />
        </Icon>
    );
}

export function ChatIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M21 12a8 8 0 0 1-8 8H7l-4 3V12a8 8 0 0 1 8-8h2a8 8 0 0 1 8 8Z" />
        </Icon>
    );
}

export function GlobeIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <circle cx="12" cy="12" r="9" />
            <path d="M3 12h18M12 3c2.5 2.7 2.5 15.3 0 18M12 3c-2.5 2.7-2.5 15.3 0 18" />
        </Icon>
    );
}

export function DownloadIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M12 4v11m0 0 4-4m-4 4-4-4" />
            <path d="M4 19h16" />
        </Icon>
    );
}

export function ArrowRightIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M5 12h14m0 0-5-5m5 5-5 5" />
        </Icon>
    );
}

export function ArrowLeftIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M19 12H5m0 0 5-5m-5 5 5 5" />
        </Icon>
    );
}

export function PhoneIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M4 5c0-1 1-2 2-2h2l2 5-2 1a12 12 0 0 0 5 5l1-2 5 2v2c0 1-1 2-2 2A15 15 0 0 1 4 5Z" />
        </Icon>
    );
}

export function ClockIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <circle cx="12" cy="12" r="9" />
            <path d="M12 7v5l3 2" />
        </Icon>
    );
}

export function CheckIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="m5 13 4 4L19 7" />
        </Icon>
    );
}

export function PinIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
            <circle cx="12" cy="10" r="2.5" />
        </Icon>
    );
}

export function UserIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <circle cx="12" cy="8" r="4" />
            <path d="M4 21a8 8 0 0 1 16 0" />
        </Icon>
    );
}

export function VideoIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <rect x="3" y="6" width="12" height="12" rx="2" />
            <path d="m15 10 6-3v10l-6-3Z" />
        </Icon>
    );
}

export function ShareIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <circle cx="18" cy="5" r="2.5" />
            <circle cx="6" cy="12" r="2.5" />
            <circle cx="18" cy="19" r="2.5" />
            <path d="m8.2 10.8 7.6-4.6M8.2 13.2l7.6 4.6" />
        </Icon>
    );
}

export function LinkIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
            <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
        </Icon>
    );
}

export function SendIcon({ className }: { className?: string }) {
    return (
        <Icon className={className}>
            <path d="M21 3 3 10.5l7 2.5 2.5 7Z" />
            <path d="m10 13 4.5-4.5" />
        </Icon>
    );
}
